// EDEN · the plan's writing boxes with light formatting — the DOM half (the format lives in rich-text-format.ts).
//
// mountRichText(textarea, { pasteLocked }) puts a small toolbar (Bold · Italic · Bulleted list · Numbered list ·
// Move left · Move right · Undo · Redo) and a typing box where the textarea was. The textarea stays in the page,
// hidden, with its id: it is still the value every page reads (.value) and listens to ("input").
//
// The rails this file keeps:
//  • No passive writes. Mounting — and refresh() — never changes textarea.value and never fires an event. Only a real
//    edit writes back. If the teacher's edits bring the box back to exactly what was loaded, the ORIGINAL text goes
//    back byte for byte, so opening (or clicking around in) a submitted plan never marks it as edited.
//  • The paste lock. A locked box (the three thinking cells and the five procedure steps) refuses paste, drop and
//    the browser's other paste-like inputs, and says so with the same calm sentence as lockPaste() in src/dom.ts.
//    An unlocked box (Materials) takes paste as PLAIN TEXT only — never another page's HTML or styles.
//  • No libraries. document.execCommand does the editing (bold, italic, lists, move left/right); what is stored comes
//    from our own DOM walk (serializeNodes), never from a browser's HTML. Undo/Redo is each box's own history: the
//    browser keeps ONE undo list for the whole page, so its undo could change a different box.
//  • Calm, plain controls for B1 readers: short names, simple icons, a soft tint for "on", dimmed (never red) when a
//    control does not apply here.

import "./styles/rich-text.css";
import { notify } from "./dom";
import { richToEditorHtml, serializeNodes } from "./rich-text-format";

export interface RichTextHandle {
  /** The box the teacher types in (contenteditable, role="textbox"). */
  editor: HTMLElement;
  /** Read textarea.value into the box again — call it after code changes the value. Never writes the textarea. */
  refresh(): void;
  /** Put the caret in the box (at the end, unless it is already inside). */
  focus(): void;
}

export interface RichTextOptions {
  /** true for the thinking cells and the procedure steps: no paste, no drop — the teacher's own English. */
  pasteLocked: boolean;
  /** Grey words shown while the box is empty (default: the textarea's own placeholder). */
  placeholder?: string;
}

type Command = "bold" | "italic" | "insertUnorderedList" | "insertOrderedList" | "outdent" | "indent" | "undo" | "redo";

interface Tool {
  cmd: Command;
  label: string;
  icon: string;
  /** Shows an on/off state (aria-pressed). */
  toggle?: boolean;
}

const BULLET = String.fromCharCode(0x2022);
/** The same words as lockPaste() in src/dom.ts. */
const PASTE_OFF = "Paste is off in this box — please type it in your own words.";
const PASTE_INPUTS = new Set(["insertFromPaste", "insertFromDrop", "insertFromPasteAsQuotation", "insertFromYank"]);
/** Styles the stored format cannot keep (e.g. iOS's B I U menu offers underline) — refused, so nothing appears that
 *  would quietly vanish after saving. */
const UNSTORED_INPUTS = new Set([
  "formatUnderline", "formatStrikeThrough", "formatSuperscript", "formatSubscript", "formatJustifyFull",
  "formatJustifyCenter", "formatJustifyRight", "formatJustifyLeft", "formatSetBlockTextDirection",
  "formatSetInlineTextDirection", "formatBackColor", "formatFontColor", "formatFontName", "insertHorizontalRule",
  "insertLink",
]);
const NAV_KEYS = new Set(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown"]);
const HISTORY_LIMIT = 100;
const GROUP_MS = 1500;

const svg = (body: string): string =>
  `<svg class="rt-ico" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="none" ` +
  `stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
const numeral = (y: number, n: number): string =>
  `<text x="5" y="${y}" text-anchor="middle" font-size="10" font-weight="700" fill="currentColor" stroke="none" ` +
  `font-family="Arial, Helvetica, sans-serif">${n}</text>`;

const GROUPS: Tool[][] = [
  [
    { cmd: "bold", label: "Bold", toggle: true, icon: `<span class="rt-glyph rt-glyph-b" aria-hidden="true">B</span>` },
    { cmd: "italic", label: "Italic", toggle: true, icon: `<span class="rt-glyph rt-glyph-i" aria-hidden="true">I</span>` },
  ],
  [
    {
      cmd: "insertUnorderedList",
      label: "Bulleted list",
      toggle: true,
      icon: svg(
        `<circle cx="5" cy="7" r="1.6" fill="currentColor" stroke="none"/><circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none"/>` +
          `<circle cx="5" cy="17" r="1.6" fill="currentColor" stroke="none"/><path d="M10 7h10M10 12h10M10 17h10"/>`,
      ),
    },
    {
      cmd: "insertOrderedList",
      label: "Numbered list",
      toggle: true,
      icon: svg(`${numeral(11, 1)}${numeral(21, 2)}<path d="M11 7.5h9M11 17.5h9"/>`), // two big numerals read at 1x
    },
  ],
  [
    { cmd: "outdent", label: "Move left", icon: svg(`<path d="M4 5h16M12 10h8M12 14h8M4 19h16"/><path d="M8 9l-3 3 3 3"/>`) },
    { cmd: "indent", label: "Move right", icon: svg(`<path d="M4 5h16M12 10h8M12 14h8M4 19h16"/><path d="M5 9l3 3-3 3"/>`) },
  ],
  [
    { cmd: "undo", label: "Undo", icon: svg(`<path d="M9 14L4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11"/>`) },
    { cmd: "redo", label: "Redo", icon: svg(`<path d="M15 14l5-5-5-5"/><path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13"/>`) },
  ],
];

const mounted = new WeakMap<HTMLTextAreaElement, RichTextHandle>();
let counter = 0;

// One document-wide selectionchange listener for every box (pages are swapped by innerHTML with no teardown, so a
// box whose page is gone simply drops out of the set the next time the selection moves).
interface Live {
  editor: HTMLElement;
  seen: boolean;
  onSelection(): void;
}
const live = new Set<Live>();
let watching = false;
function watchSelection(entry: Live): void {
  live.add(entry);
  if (watching) return;
  watching = true;
  document.addEventListener("selectionchange", () => {
    for (const l of live) {
      if (l.editor.isConnected) {
        l.seen = true;
        l.onSelection();
      } else if (l.seen) {
        live.delete(l);
      }
    }
  });
}

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

function uniqueId(doc: Document, want: string): string {
  let id = want;
  for (let n = 2; doc.getElementById(id); n++) id = `${want}-${n}`;
  return id;
}

/** A caret position that survives the box's HTML being put back: the child-index path from the box, checked against
 *  the number of characters before it (`text`), which also finds it again if the path no longer fits. */
interface Point {
  path: number[];
  offset: number;
  text: number;
  /** The caret sat at the start of a text node (so a tie goes to the next line, not the end of the one before). */
  atStart: boolean;
}
interface Snap {
  html: string;
  start: Point | null;
  end: Point | null;
}

/** Replace a textarea with the formatting box (the textarea stays, hidden, as the value store). Mounting the same
 *  textarea twice returns the first handle and changes nothing. */
export function mountRichText(textarea: HTMLTextAreaElement, opts: RichTextOptions): RichTextHandle {
  const existing = mounted.get(textarea);
  if (existing) return existing;

  const doc = textarea.ownerDocument;
  const baseId = textarea.id || `rt-${++counter}`;

  const box = doc.createElement("div");
  box.className = "rt-box";
  if (textarea.classList.contains("step-box")) box.classList.add("rt-step");

  const bar = doc.createElement("div");
  bar.className = "rt-bar";
  bar.setAttribute("role", "toolbar");
  bar.setAttribute("aria-label", "Text style");
  const tools = doc.createElement("div");
  tools.className = "rt-tools";

  const editor = doc.createElement("div");
  editor.className = "rt-editor";
  editor.id = uniqueId(doc, `${baseId}-editor`);
  editor.setAttribute("role", "textbox");
  editor.setAttribute("aria-multiline", "true");
  editor.setAttribute("spellcheck", "true");
  editor.setAttribute("autocapitalize", "sentences");
  editor.setAttribute("translate", "no"); // page translation must never rewrite what the teacher is typing
  bar.setAttribute("aria-controls", editor.id);

  const placeholder = opts.placeholder ?? textarea.getAttribute("placeholder") ?? "";
  if (placeholder) {
    editor.dataset.placeholder = placeholder;
    editor.setAttribute("aria-placeholder", placeholder);
  }
  const described = textarea.getAttribute("aria-describedby");
  if (described) editor.setAttribute("aria-describedby", described);

  // ── the toolbar ──
  const buttons: HTMLButtonElement[] = [];
  const toggles = new Map<Command, HTMLButtonElement>();
  const byCmd = new Map<Command, HTMLButtonElement>();
  GROUPS.forEach((group, gi) => {
    if (gi > 0) {
      const sep = doc.createElement("span");
      sep.className = "rt-sep";
      sep.setAttribute("aria-hidden", "true");
      tools.appendChild(sep);
    }
    for (const tool of group) {
      const btn = doc.createElement("button");
      btn.type = "button";
      btn.className = "rt-btn";
      btn.dataset.cmd = tool.cmd;
      btn.title = tool.label;
      btn.setAttribute("aria-label", tool.label);
      btn.innerHTML = tool.icon;
      btn.tabIndex = buttons.length === 0 ? 0 : -1; // one stop in the Tab order; arrow keys move along the bar
      if (tool.toggle) {
        btn.setAttribute("aria-pressed", "false");
        toggles.set(tool.cmd, btn);
      }
      // mousedown would move focus (and the caret) out of the text — keep both where the teacher left them.
      btn.addEventListener("mousedown", (e) => e.preventDefault());
      btn.addEventListener("click", () => run(tool.cmd));
      tools.appendChild(btn);
      buttons.push(btn);
      byCmd.set(tool.cmd, btn);
    }
  });
  bar.appendChild(tools);
  bar.addEventListener("keydown", (e) => {
    const at = buttons.indexOf(doc.activeElement as HTMLButtonElement);
    if (at < 0) return;
    let to = -1;
    if (e.key === "ArrowRight") to = (at + 1) % buttons.length;
    else if (e.key === "ArrowLeft") to = (at - 1 + buttons.length) % buttons.length;
    else if (e.key === "Home") to = 0;
    else if (e.key === "End") to = buttons.length - 1;
    if (to < 0) return;
    e.preventDefault();
    buttons.forEach((b, k) => (b.tabIndex = k === to ? 0 : -1));
    buttons[to]!.focus();
  });
  // On a narrow phone the bar slides sideways; a soft fade at an edge says "more this way".
  const fade = (): void => {
    const hidden = tools.scrollWidth - tools.clientWidth;
    tools.classList.toggle("rt-more-r", hidden > 1 && tools.scrollLeft < hidden - 1);
    tools.classList.toggle("rt-more-l", hidden > 1 && tools.scrollLeft > 1);
  };
  tools.addEventListener("scroll", fade, { passive: true });
  if (typeof ResizeObserver !== "undefined") new ResizeObserver(fade).observe(tools);

  box.appendChild(bar);
  box.appendChild(editor);
  textarea.insertAdjacentElement("afterend", box);
  textarea.hidden = true; // stays in the DOM with its id: the value store other code reads
  textarea.classList.add("rt-source");

  // ── state ──
  let loaded = ""; // textarea.value as last read into the box
  let baseline = ""; // serializeNodes(editor) right after that read: what "unchanged" looks like
  let known = ""; // the value this box last read or wrote (to notice code changing it)
  let writing = false; // our own "input" event on the textarea is being dispatched
  let converting = false; // "- " → list conversion in progress (its inner input events are ours)
  let saved: Range | null = null; // the last caret/selection inside the box
  let internalDrag = false;
  let wasActive = false;
  let inputs = 0; // input events seen (to tell whether a command produced one)

  const editable = (): boolean => !textarea.disabled && !textarea.readOnly;

  const showEmpty = (): void => {
    const empty = (editor.textContent ?? "") === "" && !editor.querySelector("li");
    editor.classList.toggle("is-empty", empty);
  };

  // ── selection helpers ──
  const selRange = (): Range | null => {
    const sel = doc.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const r = sel.getRangeAt(0);
    return editor.contains(r.commonAncestorContainer) ? r : null;
  };
  const currentItem = (): HTMLElement | null => {
    const r = selRange();
    for (let n: Node | null = r ? r.startContainer : null; n && n !== editor; n = n.parentNode) {
      if (n.nodeName === "LI") return n as HTMLElement;
    }
    return null;
  };
  const depth = (li: HTMLElement): number => {
    let d = 0;
    for (let n: Node | null = li; n && n !== editor; n = n.parentNode) if (n.nodeName === "UL" || n.nodeName === "OL") d++;
    return d;
  };
  /** A point can move right only under a point above it (so a list never starts with an orphan sub-point). */
  const canIndent = (li: HTMLElement | null): boolean => !!li && !!li.previousElementSibling;

  const setCaret = (node: Node, offset: number): void => {
    const sel = doc.getSelection();
    if (!sel) return;
    const r = doc.createRange();
    r.setStart(node, offset);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  };

  const caretToEnd = (): void => {
    let node: Node = editor;
    while (node.lastChild && node.lastChild.nodeName !== "BR") node = node.lastChild;
    if (node.lastChild) {
      // a placeholder <br>: the caret goes just before it
      setCaret(node, node.childNodes.length - 1);
    } else if (node.nodeType === Node.TEXT_NODE) {
      setCaret(node, (node as Text).length);
    } else {
      setCaret(node, node.childNodes.length);
    }
  };

  const focusEditor = (): void => {
    const inside = !!selRange();
    editor.focus();
    if (!inside) caretToEnd();
  };

  /** Before a toolbar command: the box has focus and the caret is where the teacher left it. */
  const ensureSelection = (): void => {
    const inside = !!selRange();
    if (doc.activeElement !== editor) editor.focus({ preventScroll: true });
    if (inside) return;
    const sel = doc.getSelection();
    if (sel && saved && editor.contains(saved.startContainer)) {
      sel.removeAllRanges();
      sel.addRange(saved);
    } else {
      caretToEnd();
    }
  };

  // ── caret positions that survive putting HTML back (history, list switching) ──
  const textBefore = (node: Node, offset: number): number => {
    const r = doc.createRange();
    r.selectNodeContents(editor);
    r.setEnd(node, offset);
    return r.toString().length;
  };
  const pointOf = (node: Node, offset: number): Point => {
    const path: number[] = [];
    for (let n: Node = node; n !== editor && n.parentNode; n = n.parentNode) {
      path.unshift(Array.prototype.indexOf.call(n.parentNode.childNodes, n) as number);
    }
    return { path, offset, text: textBefore(node, offset), atStart: offset === 0 };
  };
  /** The node + offset `text` characters into the box (a tie at a line edge goes by `atStart`). */
  const byText = (text: number, atStart: boolean): [Node, number] | null => {
    const walker = doc.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    let seen = 0;
    let last: Text | null = null;
    for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
      const len = n.length;
      if (text < seen + len || (text === seen + len && !atStart)) return [n, text - seen];
      seen += len;
      last = n;
    }
    return last ? [last, last.length] : null;
  };
  const locate = (p: Point): [Node, number] | null => {
    let n: Node = editor;
    for (const i of p.path) {
      const child = n.childNodes[i];
      if (!child) return byText(p.text, p.atStart);
      n = child;
    }
    const max = n.nodeType === Node.TEXT_NODE ? (n as Text).length : n.childNodes.length;
    if (p.offset > max || textBefore(n, p.offset) !== p.text) return byText(p.text, p.atStart);
    return [n, p.offset];
  };

  // ── history: this box's own undo / redo ──
  const past: Snap[] = [];
  const future: Snap[] = [];
  let now: Snap = { html: "", start: null, end: null };
  let group = "";
  let groupAt = 0;
  let groupHasWord = false; // the typing step so far holds more than spaces
  let fresh = true; // the next change starts a new undo step

  const markSelection = (s: Snap): void => {
    const r = selRange();
    s.start = r ? pointOf(r.startContainer, r.startOffset) : null;
    s.end = r ? pointOf(r.endContainer, r.endOffset) : null;
  };
  const capture = (): Snap => {
    const s: Snap = { html: editor.innerHTML, start: null, end: null };
    markSelection(s);
    return s;
  };
  const showHistory = (): void => {
    byCmd.get("undo")?.setAttribute("aria-disabled", past.length ? "false" : "true");
    byCmd.get("redo")?.setAttribute("aria-disabled", future.length ? "false" : "true");
  };
  /** After a change: one undo step per word typed, per run of deleting, and per command. */
  const record = (type: string, data: string | null): void => {
    const next = capture();
    if (next.html === now.html) {
      now = next;
      return;
    }
    const kind = /^insert(Text|CompositionText|ReplacementText)$/.test(type)
      ? "type"
      : /^delete(Content|Word|SoftLine|HardLine)/.test(type)
        ? "delete"
        : "other";
    const t = Date.now();
    const join = !fresh && kind !== "other" && kind === group && t - groupAt < GROUP_MS;
    if (!join) {
      past.push(now);
      if (past.length > HISTORY_LIMIT) past.shift();
    }
    now = next;
    future.length = 0;
    group = kind;
    groupAt = t;
    const spaces = !!data && !/\S/.test(data);
    groupHasWord = (join && groupHasWord) || (kind === "type" && !!data && !spaces);
    // a space after a word ends that word's step (a space before any word joins the word that follows)
    fresh = kind === "other" || (kind === "type" && spaces && groupHasWord);
    showHistory();
  };
  const restore = (s: Snap): void => {
    editor.innerHTML = s.html;
    saved = null;
    if (doc.activeElement !== editor) editor.focus({ preventScroll: true });
    const a = s.start ? locate(s.start) : null;
    const b = s.end ? locate(s.end) : null;
    const sel = doc.getSelection();
    if (a && sel) {
      const r = doc.createRange();
      r.setStart(a[0], a[1]);
      if (b) r.setEnd(b[0], b[1]); // an end before the start collapses the range — fine
      sel.removeAllRanges();
      sel.addRange(r);
    } else {
      caretToEnd();
    }
    fresh = true;
    sync();
    updateState();
    showHistory();
  };
  const undo = (): void => {
    const s = past.pop();
    if (!s) return;
    future.push(now);
    now = s;
    restore(s);
  };
  const redo = (): void => {
    const s = future.pop();
    if (!s) return;
    past.push(now);
    now = s;
    restore(s);
  };

  // ── reading and writing the textarea ──
  const render = (asStep: boolean): void => {
    const before = editor.innerHTML;
    loaded = textarea.value;
    editor.innerHTML = richToEditorHtml(loaded);
    baseline = serializeNodes(editor);
    known = loaded;
    saved = null;
    const on = editable();
    editor.contentEditable = on ? "true" : "false";
    editor.setAttribute("aria-readonly", on ? "false" : "true");
    for (const b of buttons) b.disabled = !on;
    showEmpty();
    // Code put new words in (e.g. "Add these books to Materials"): one undo step takes them back out.
    if (asStep && editor.innerHTML !== before) {
      past.push(now);
      if (past.length > HISTORY_LIMIT) past.shift();
      future.length = 0;
    }
    now = capture();
    fresh = true;
    showHistory();
  };

  /** The box → textarea.value, then one bubbling "input" so autosave, counters and checks run as before. */
  const sync = (): void => {
    showEmpty();
    const text = serializeNodes(editor);
    const next = text === baseline ? loaded : text;
    known = next;
    if (next === textarea.value) return;
    textarea.value = next;
    writing = true;
    try {
      textarea.dispatchEvent(new Event("input", { bubbles: true }));
    } finally {
      writing = false;
    }
  };

  // Code that sets textarea.value and fires "input" shows up here at once; code that changes the value silently
  // calls handle.refresh().
  textarea.addEventListener("input", () => {
    if (!writing && textarea.value !== known) {
      render(true);
      updateState();
    }
  });

  const prepare = (): void => {
    try {
      doc.execCommand("styleWithCSS", false, "false"); // <b>/<i>, never style spans
      doc.execCommand("defaultParagraphSeparator", false, "div"); // a new line is a <div> in every browser
    } catch {
      /* older engines: their defaults are the same */
    }
  };

  const updateState = (): void => {
    const active = !!selRange() && box.contains(doc.activeElement);
    for (const [cmd, btn] of toggles) {
      let on = false;
      if (active) {
        try {
          on = doc.queryCommandState(cmd);
        } catch {
          on = false;
        }
      }
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    }
    const li = active ? currentItem() : null;
    byCmd.get("indent")?.setAttribute("aria-disabled", canIndent(li) ? "false" : "true");
    byCmd.get("outdent")?.setAttribute("aria-disabled", li ? "false" : "true");
    wasActive = active;
  };

  const exec = (cmd: Command, markCaret = true): void => {
    prepare();
    if (markCaret && now.html === editor.innerHTML) markSelection(now); // undo puts the caret back here
    const seen = inputs;
    fresh = true;
    try {
      doc.execCommand(cmd, false);
    } catch {
      /* not supported here: nothing changes */
    }
    if (inputs === seen) record("command", null); // the browser fired no input event: keep the step anyway
    fresh = true;
    sync();
    updateState();
  };

  /** With the caret in a point (nothing selected), Bulleted list / Numbered list switches the whole list, not just
   *  that one point — what a teacher means by "make this list numbered". */
  const switchWholeList = (cmd: Command, li: HTMLElement): boolean => {
    const list = li.parentElement;
    const r = selRange();
    const want = cmd === "insertOrderedList" ? "OL" : "UL";
    if (!list || !r || !r.collapsed || (list.nodeName !== "UL" && list.nodeName !== "OL") || list.nodeName === want) return false;
    const keep = pointOf(r.startContainer, r.startOffset);
    const sel = doc.getSelection();
    if (!sel) return false;
    if (now.html === editor.innerHTML) markSelection(now); // undo puts the caret back here, not the whole list
    const all = doc.createRange();
    all.selectNodeContents(list);
    sel.removeAllRanges();
    sel.addRange(all);
    exec(cmd, false);
    const back = byText(keep.text, keep.atStart);
    if (back) setCaret(back[0], back[1]);
    markSelection(now);
    updateState();
    return true;
  };

  const run = (cmd: Command): void => {
    if (!editable()) return;
    if (cmd === "undo" || cmd === "redo") {
      if (cmd === "undo") undo();
      else redo();
      if (doc.activeElement !== editor) editor.focus({ preventScroll: true });
      return;
    }
    ensureSelection();
    const li = currentItem();
    if (cmd === "indent" && !canIndent(li)) return;
    if (cmd === "outdent" && !li) return;
    if ((cmd === "insertOrderedList" || cmd === "insertUnorderedList") && li && switchWholeList(cmd, li)) return;
    exec(cmd);
  };

  // A line ends at a <br>, a raw "\n" (Chrome's Shift+Enter in a pre-wrap box) or a block's edge. Inline wrappers
  // (<b>, <i>, <span>…) do not end it: bold carried onto a new line (as in Word) is still the same line.
  const LINE_ENDS = /^(BR|DIV|P|LI|UL|OL|H[1-6]|BLOCKQUOTE|PRE|TABLE|TBODY|THEAD|TR|TD|TH|SECTION|ARTICLE)$/;
  /** The text of the line that ends where `n` ends (n: a copy of everything from the block's start to the caret). */
  const lineTextBefore = (n: Node, acc: { text: string }): void => {
    for (const c of Array.from(n.childNodes)) {
      if (c.nodeType === Node.TEXT_NODE) {
        const d = (c as Text).data;
        const k = d.lastIndexOf("\n");
        acc.text = k >= 0 ? d.slice(k + 1) : acc.text + d;
      } else if (LINE_ENDS.test(c.nodeName)) {
        acc.text = "";
      } else {
        lineTextBefore(c, acc);
      }
    }
  };
  /** The text of the line that starts where `n` starts (n: a copy of everything from the caret to the block's end). */
  const lineTextAfter = (n: Node, acc: { text: string; done: boolean }): void => {
    for (const c of Array.from(n.childNodes)) {
      if (acc.done) return;
      if (c.nodeType === Node.TEXT_NODE) {
        const d = (c as Text).data;
        const k = d.indexOf("\n");
        if (k >= 0) {
          acc.text += d.slice(0, k);
          acc.done = true;
        } else {
          acc.text += d;
        }
      } else if (LINE_ENDS.test(c.nodeName)) {
        acc.done = true;
      } else {
        lineTextAfter(c, acc);
      }
    }
  };

  /** Typing "- " (or "* ", "• ", "1. ", "1) ") at the start of an empty line turns the line into a list — also when
   *  the line carries bold or italic (the marker then sits inside <b>/<i>). */
  const autoList = (): boolean => {
    const sel = doc.getSelection();
    if (!sel || !sel.isCollapsed || !sel.anchorNode || sel.anchorNode.nodeType !== Node.TEXT_NODE) return false;
    const node = sel.anchorNode as Text;
    const offset = sel.anchorOffset;
    if (!editor.contains(node)) return false;
    // Quick no: a space that does not end a marker (or sits alone in its own styled piece of text).
    const local = node.data.slice(0, offset).replace(/\xa0/g, " ");
    if (local !== " " && !["- ", "* ", `${BULLET} `, ". ", ") "].includes(local.slice(-2))) return false;
    // The line's block: the nearest <div>/<p>, or the box itself. Inside a point already → nothing to do.
    let block: Node | null = node.parentNode;
    while (block && block !== editor && block.nodeName !== "DIV" && block.nodeName !== "P") {
      if (LINE_ENDS.test(block.nodeName)) return false; // <li>, <td>, … — not a plain line
      block = block.parentNode;
    }
    if (!block) return false;
    const head = doc.createRange();
    head.setStart(block, 0);
    head.setEnd(node, offset);
    const before = { text: "" };
    lineTextBefore(head.cloneContents(), before);
    const tail = doc.createRange();
    tail.setStart(node, offset);
    tail.setEnd(block, block.childNodes.length);
    const after = { text: "", done: false };
    lineTextAfter(tail.cloneContents(), after);
    if (after.text.trim() !== "") return false;
    const typed = before.text.replace(/\xa0/g, " ");
    const cmd: Command | null =
      typed === "- " || typed === "* " || typed === `${BULLET} `
        ? "insertUnorderedList"
        : typed === "1. " || typed === "1) "
          ? "insertOrderedList"
          : null;
    if (!cmd) return false;
    // Where the marker starts: `typed.length` characters back, all on this line (so all in text nodes).
    let start: Text = node;
    let at = offset - typed.length;
    if (at < 0) {
      const walker = doc.createTreeWalker(block, NodeFilter.SHOW_TEXT);
      walker.currentNode = node;
      let left = typed.length - offset;
      for (let p = walker.previousNode() as Text | null; p; p = walker.previousNode() as Text | null) {
        if (left <= p.length) {
          start = p;
          at = p.length - left;
          break;
        }
        left -= p.length;
      }
      if (at < 0) return false;
    }
    converting = true;
    try {
      const r = doc.createRange();
      r.setStart(start, at);
      r.setEnd(node, offset);
      sel.removeAllRanges();
      sel.addRange(r);
      prepare();
      doc.execCommand("delete", false);
      doc.execCommand(cmd, false);
    } catch {
      /* leave the typed text as it is */
    } finally {
      converting = false;
    }
    return true;
  };

  const say = (): void => notify(PASTE_OFF);

  /** Show the box exactly as it will be stored — after a paste, pasted "- a" lines are points at once, not only on
   *  the next visit. The caret keeps its place, counted from the end of the box (the pasted part is before it). */
  const showAsStored = (): void => {
    const html = richToEditorHtml(serializeNodes(editor));
    if (html === editor.innerHTML) return;
    const r = selRange();
    let after: number | null = null;
    let atStart = false;
    if (r) {
      const tail = doc.createRange();
      tail.selectNodeContents(editor);
      tail.setStart(r.endContainer, r.endOffset);
      after = tail.toString().length;
      atStart = r.endOffset === 0;
    }
    editor.innerHTML = html;
    saved = null;
    const at = after === null ? null : byText((editor.textContent ?? "").length - after, atStart);
    if (at) setCaret(at[0], at[1]);
    else caretToEnd();
  };

  /** Plain text in at the caret (unlocked box): the clipboard's own HTML and styles never come in. */
  const insertPlain = (text: string): void => {
    const clean = text.replace(/\r\n?/g, "\n").replace(/\xa0/g, " ");
    if (!clean) return;
    prepare();
    if (now.html === editor.innerHTML) markSelection(now);
    fresh = true; // a paste is its own undo step
    const seen = inputs;
    doc.execCommand("insertText", false, clean);
    if (inputs === seen) record("insertFromPaste", null); // the browser fired no input event: keep the step anyway
    showAsStored();
    now = capture(); // the step above already holds the state before the paste
    fresh = true;
    sync();
    updateState();
  };

  // ── events ──
  editor.addEventListener("beforeinput", (e) => {
    const type = e.inputType;
    if (type === "historyUndo" || type === "historyRedo") {
      e.preventDefault(); // the page-wide browser undo could change another box
      if (type === "historyUndo") undo();
      else redo();
      return;
    }
    if (PASTE_INPUTS.has(type) && opts.pasteLocked) {
      e.preventDefault();
      say();
      return;
    }
    if (UNSTORED_INPUTS.has(type)) {
      e.preventDefault();
      return;
    }
    if (now.html === editor.innerHTML) markSelection(now); // undo puts the caret back where this change began
  });

  editor.addEventListener("input", (e) => {
    if (converting) return;
    const ie = e as InputEvent;
    inputs++;
    record(ie.inputType ?? "", ie.data ?? null);
    if (ie.inputType === "insertText" && (ie.data === " " || ie.data === "\xa0") && autoList()) record("autoList", null);
    sync();
    updateState();
  });
  editor.addEventListener("compositionend", () => sync());

  editor.addEventListener("paste", (e) => {
    e.preventDefault();
    if (opts.pasteLocked) {
      say();
      return;
    }
    insertPlain(e.clipboardData?.getData("text/plain") ?? "");
  });

  editor.addEventListener("dragstart", () => (internalDrag = true));
  editor.addEventListener("dragend", () => (internalDrag = false));
  editor.addEventListener("dragover", (e) => {
    if (opts.pasteLocked) e.preventDefault(); // as lockPaste: the drop event then fires and is refused
  });
  editor.addEventListener("drop", (e) => {
    if (opts.pasteLocked) {
      e.preventDefault();
      say();
      return;
    }
    if (internalDrag) return; // moving the teacher's own words inside this box
    e.preventDefault();
    const text = e.dataTransfer?.getData("text/plain") ?? "";
    if (!text) return;
    editor.focus({ preventScroll: true });
    const sel = doc.getSelection();
    const at = caretAt(doc, e.clientX, e.clientY);
    if (sel && at && editor.contains(at.startContainer)) {
      sel.removeAllRanges();
      sel.addRange(at);
    }
    insertPlain(text);
  });

  editor.addEventListener("mousedown", () => (fresh = true));
  editor.addEventListener("touchstart", () => (fresh = true), { passive: true });

  /** The caret is at the very start of a point (nothing typed before it in that point). */
  const atItemStart = (li: HTMLElement): boolean => {
    const r = selRange();
    if (!r || !r.collapsed) return false;
    const before = doc.createRange();
    before.setStart(li, 0);
    before.setEnd(r.startContainer, r.startOffset);
    if (before.toString() !== "") return false;
    const frag = before.cloneContents();
    return !frag.querySelector || !frag.querySelector("br, li, ul, ol, div, p");
  };

  editor.addEventListener("keydown", (e) => {
    if (e.isComposing) return;
    if (NAV_KEYS.has(e.key)) fresh = true;
    // Backspace at the start of a point takes the point off first (a sub-point moves left; a point becomes a plain
    // line), as in Google Docs and Word — a second Backspace then joins the line to the one above.
    if (e.key === "Backspace" && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const li = currentItem();
      if (li && atItemStart(li)) {
        e.preventDefault();
        if (depth(li) > 1) exec("outdent");
        else exec(li.parentElement?.nodeName === "OL" ? "insertOrderedList" : "insertUnorderedList");
        return;
      }
    }
    if (e.key === "Tab" && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // Only inside a point, and only when it can move — otherwise Tab moves on to the next field as usual.
      const li = currentItem();
      if (!li) return;
      if (e.shiftKey) {
        if (depth(li) > 1) {
          e.preventDefault();
          exec("outdent");
        }
      } else if (canIndent(li)) {
        e.preventDefault();
        exec("indent");
      }
      return;
    }
    const mod = IS_MAC ? e.metaKey : e.ctrlKey;
    if (!mod || e.altKey) return;
    const key = e.key.length === 1 ? e.key.toLowerCase() : "";
    const latin = /^[a-z]$/.test(key);
    const is = (letter: string): boolean => key === letter || (!latin && e.code === `Key${letter.toUpperCase()}`);
    if (is("z")) {
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    } else if (is("y") && !IS_MAC && !e.shiftKey) {
      e.preventDefault();
      redo();
    } else if (e.shiftKey) {
      return;
    } else if (is("b") || is("i")) {
      e.preventDefault();
      exec(is("b") ? "bold" : "italic");
    } else if (is("u")) {
      e.preventDefault(); // no underline in this format
    }
  });

  editor.addEventListener("focus", () => {
    prepare();
    updateState();
  });
  editor.addEventListener("blur", () => {
    fresh = true;
    sync();
    updateState();
  });

  watchSelection({
    editor,
    seen: false,
    onSelection: () => {
      const r = selRange();
      if (r) saved = r.cloneRange();
      if (r || wasActive) updateState();
    },
  });

  // ── the label: names the box, and a tap on it puts the caret in the box ──
  const label = textarea.labels?.[0] ?? null;
  if (label) {
    if (!label.id) label.id = uniqueId(doc, `${baseId}-label`);
    editor.setAttribute("aria-labelledby", label.id);
    label.addEventListener("click", (e) => {
      const target = e.target as Element | null;
      if (target && target !== label && target.closest("a, button, input, select, textarea, [contenteditable]")) return;
      e.preventDefault();
      focusEditor();
    });
  } else {
    const name = textarea.getAttribute("aria-label") || placeholder;
    if (name) editor.setAttribute("aria-label", name);
  }

  // Page code that focuses or scrolls to a box by its textarea (e.g. "show me the box Submit still needs") keeps
  // working: the hidden textarea hands both to the box.
  textarea.focus = (): void => focusEditor();
  textarea.scrollIntoView = (arg?: boolean | ScrollIntoViewOptions): void => box.scrollIntoView(arg);

  render(false);
  updateState();

  const handle: RichTextHandle = {
    editor,
    refresh: () => {
      render(true);
      updateState();
    },
    focus: focusEditor,
  };
  mounted.set(textarea, handle);
  return handle;
}

/** The caret position under a point on screen (Chrome/Safari: caretRangeFromPoint, Firefox: caretPositionFromPoint). */
function caretAt(doc: Document, x: number, y: number): Range | null {
  const d = doc as Document & {
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  };
  if (typeof d.caretRangeFromPoint === "function") return d.caretRangeFromPoint(x, y);
  if (typeof d.caretPositionFromPoint === "function") {
    const p = d.caretPositionFromPoint(x, y);
    if (!p) return null;
    const r = doc.createRange();
    r.setStart(p.offsetNode, p.offset);
    r.collapse(true);
    return r;
  }
  return null;
}
